import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory, getLocalizedAgentFeatures, formatPriceWithCurrency, Currency } from '../utils/i18n';
import React, { useState, useEffect } from 'react';
import { 
  Sparkles, ArrowLeft, ArrowRight, ShieldCheck, 
  FileText, Table, Languages, FileCheck, Layers, 
  FileType, Building2, Calculator, CheckSquare, Coins
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';

interface OfficeCenterHubViewProps {
  onLaunchAgent: (target: string) => void;
  onBack: () => void;
  language: 'uk' | 'en' | 'pl';
  currency?: Currency;
  currentUserEmail?: string;
}

export const OFFICE_AGENT_IDS = [
  'office-spreadsheet-agent',
  'dstu-order-constructor',
  'dstu-pkmu-auditor',
  'doc-translator',
  'corrector',
  'md-docx-converter',
  'meeting-transcriber',
  'dls-pharma-verifier',
  'hr-order-instructor',
  'pharmacy-licensing-auditor',
  'gdp-gsp-warehouse-auditor',
  'sygnalisci-whistleblowing-auditor',
  'mobilization-booking-critical-enterprise-auditor'
];

// Helper icon renderer
const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <FileText className={className} />;
  return <IconComponent className={className} />;
};

export default function OfficeCenterHubView({
  onLaunchAgent,
  onBack,
  language, currentUserEmail, currency = 'UAH'
}: OfficeCenterHubViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';
  const [selectedSubTab, setSelectedSubTab] = useState<'all' | 'spreadsheets' | 'documents' | 'translation'>('all');
  const [pricing, setPricing] = useState<Record<string, { price: number; limit: number; durationDays: number; singlePrice: number }>>({});

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await fetch('/api/subscription/pricing');
        if (res.ok) {
          const data = await res.json();
          setPricing(data);
        }
      } catch (err) {
        console.error('Failed to fetch office pricing:', err);
      }
    };
    fetchPricing();
  }, []);

  const allOfficeAgents = agentsConfig.filter((a: any) => OFFICE_AGENT_IDS.includes(a.id));

  const filteredAgents = allOfficeAgents.filter((agent: any) => {
    if (selectedSubTab === 'all') return true;
    if (selectedSubTab === 'spreadsheets') {
      return agent.id === 'office-spreadsheet-agent';
    }
    if (selectedSubTab === 'documents') {
      return ['dstu-order-constructor', 'dstu-pkmu-auditor', 'corrector', 'md-docx-converter', 'meeting-transcriber'].includes(agent.id);
    }
    if (selectedSubTab === 'translation') {
      return agent.id === 'doc-translator';
    }
    return true;
  });

  return (
    <div className="space-y-8 animate-fade-in pb-12 font-mono">
      {/* Header Bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-slate-800 rounded-xl p-4 text-white">
        <button
          onClick={onBack}
          className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{isPl ? '← Powrót do Panelu Głównego' : isUk ? '← Назад до Головного Хабу' : '← Back to Main Hub'}</span>
        </button>

        <div className="flex items-center gap-2">
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-md">
            {isPl ? '🏢 Moduły Biurowe, Arkusze & Word' : isUk ? '🏢 Центр Офісних Додатків & Таблиць' : '🏢 Office Applications & Spreadsheet Hub'}
          </span>
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-blue-500/20 text-blue-300 border border-blue-400/30 rounded-md flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-blue-400" />
            {isPl ? '100% Zgodność z Normami Dokumentacji' : isUk ? '100% ДСТУ 4163:2020 Сумісність' : '100% Standard Compliant'}
          </span>
        </div>
      </div>

      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-slate-950 via-emerald-950 to-slate-900 border border-emerald-500/40 rounded-2xl p-6 sm:p-8 text-white shadow-2xl overflow-hidden">
        <div className="absolute right-0 top-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-3xl space-y-3">
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 rounded">
              {isPl ? 'ARKUSZE EXCEL & CSV • KOREKTA TEKSTÓW • TŁUMACZENIA • FORMAT DOCX' : isUk ? 'EXCEL & CSV ТАБЛИЦІ • ДІЛОВОДСТВО ДСТУ 4163 • ПЕРЕКЛАД & РЕДАГУВАННЯ' : 'EXCEL/CSV SPREADSHEETS • PROOFREADING • TRANSLATION • WORD DOCX'}
            </span>
          </div>

          <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight uppercase leading-tight bg-gradient-to-r from-white via-slate-100 to-emerald-300 bg-clip-text text-transparent">
            {isPl 
              ? 'CENTRUM APLIKACJI BIUROWYCH & DOKUMENTÓW' 
              : isUk 
              ? 'ЦЕНТР ОФІСНИХ ДОДАТКІВ & ДОКУМЕНТООБІГУ' 
              : 'OFFICE APPLICATIONS & DOCUMENT HUB'}
          </h1>

          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
            {isPl 
              ? 'Specjalistyczne narzędzia do automatyzacji biura: przetwarzanie i sumowanie warunkowe tabel Excel/CSV (SUMIF), audyt normatywny dokumentów, korekta językowa, tłumaczenia oraz konwersja Markdown do Word (.docx).'
              : isUk 
              ? 'Спеціалізовані інструменти для автоматизації офісної рутини: обробка та умовне підсумовування (SUMIF) таблиць Excel/CSV, нормативний аудит документів за ДСТУ 4163:2020, коректура текстів, переклад та миттєва конвертація Markdown у Word (.docx).'
              : 'Specialized AI agents for office productivity: Excel/CSV spreadsheet processing & conditional summing (SUMIF), document auditing, proofreading, document translation, and Markdown to Word conversion.'}
          </p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 border-b border-slate-200">
        <button
          onClick={() => setSelectedSubTab('all')}
          className={`px-4 py-2 text-xs font-extrabold uppercase rounded-xl transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'all'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/20'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          {isPl ? 'Wszyscy Agenci Biurowi' : isUk ? 'Усі Офісні Агенти' : 'All Office Agents'} ({allOfficeAgents.length})
        </button>

        <button
          onClick={() => setSelectedSubTab('spreadsheets')}
          className={`px-4 py-2 text-xs font-extrabold uppercase rounded-xl transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
            selectedSubTab === 'spreadsheets'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/20'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          <Table className="w-3.5 h-3.5" />
          <span>{isPl ? '📊 Tabele & Excel (SUMIF)' : isUk ? '📊 Таблиці & Excel (SUMIF)' : '📊 Spreadsheets & SUMIF'}</span>
        </button>

        <button
          onClick={() => setSelectedSubTab('documents')}
          className={`px-4 py-2 text-xs font-extrabold uppercase rounded-xl transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
            selectedSubTab === 'documents'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/20'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          <FileText className="w-3.5 h-3.5" />
          <span>{isPl ? '📄 Obieg Dokumentów & Normy' : isUk ? '📄 Документообіг & ДСТУ 4163' : '📄 Document Standards'}</span>
        </button>

        <button
          onClick={() => setSelectedSubTab('translation')}
          className={`px-4 py-2 text-xs font-extrabold uppercase rounded-xl transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
            selectedSubTab === 'translation'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/20'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          <Languages className="w-3.5 h-3.5" />
          <span>{isPl ? '🌐 Tłumaczenie Dokumentów' : isUk ? '🌐 Переклад Документів' : '🌐 Translation'}</span>
        </button>
      </div>

      {/* Agents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAgents.map((agent: any) => {
          const agentPriceInfo = pricing[agent.id] || pricing['contract-auditor'] || { price: 490, limit: 30, singlePrice: 25 };

          return (
            <div
              key={agent.id}
              className="bg-white border border-slate-200 hover:border-emerald-500/50 rounded-2xl p-6 transition-all duration-200 shadow-sm hover:shadow-xl flex flex-col justify-between group relative overflow-hidden"
            >
              <div className="space-y-4 z-10">
                {/* Header Badge & Icon */}
                <div className="flex items-center justify-between gap-3">
                  <div className="w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-600 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                    <AgentIcon name={agent.icon} className="w-6 h-6" />
                  </div>
                  <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-md">
                    {getLocalizedAgentCategory(agent, language)}
                  </span>
                </div>

                {/* Agent Name */}
                <h3 className="text-base font-extrabold text-slate-900 group-hover:text-emerald-700 transition-colors uppercase tracking-tight">
                  {getLocalizedAgentName(agent, language)}
                </h3>

                {/* Description */}
                <p className="text-xs text-slate-600 leading-relaxed min-h-[50px]">
                  {getLocalizedAgentDesc(agent, language)}
                </p>

                {/* Features list */}
                <ul className="space-y-1.5 pt-2 border-t border-slate-100 text-[11px] text-slate-700">
                  {getLocalizedAgentFeatures(agent, language).slice(0, 4).map((feat: string, idx: number) => (
                    <li key={idx} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 shrink-0" />
                      <span className="truncate">{feat}</span>
                    </li>
                  ))}
                </ul>

                {/* PRICING & MODEL BADGES */}
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-1.5 text-[10px]">
                  <div className="flex items-center justify-between font-bold text-slate-800">
                    <span className="flex items-center gap-1 text-slate-600">
                      <Sparkles className="w-3 h-3 text-emerald-600" />
                      {isPl ? 'Model:' : isUk ? 'Модель:' : 'Model:'}
                    </span>
                    <span className="text-emerald-700 font-extrabold">
                      ✨ Gemini 3.7 Flash
                    </span>
                  </div>

                  <div className="flex items-center justify-between font-bold text-slate-800">
                    <span className="text-slate-600">{isUk ? 'Підписка:' : isPl ? 'Subskrypcja:' : 'Subscription:'}</span>
                    <span>{formatPriceWithCurrency(agentPriceInfo.price, currency || 'UAH')} / 30 {isUk ? 'запитів' : isPl ? 'zapytań' : 'requests'}</span>
                  </div>

                  <div className="flex items-center justify-between font-bold text-slate-800">
                    <span className="text-slate-600">{isUk ? 'Разовий запуск:' : isPl ? 'Pojedynczo:' : 'Single Run:'}</span>
                    <span className="text-emerald-700 font-extrabold">{formatPriceWithCurrency(agentPriceInfo.singlePrice, currency || 'UAH')}</span>
                  </div>

                  <div className="pt-1 border-t border-slate-200 flex items-center justify-between font-extrabold text-emerald-600">
                    <span>{isPl ? '🎁 Testowy start:' : isUk ? '🎁 Тестовий запуск:' : '🎁 Trial run:'}</span>
                    <span className="bg-emerald-600 text-white px-1.5 py-0.5 rounded text-[9px] uppercase">
                      100% FREE
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <button
                onClick={() => onLaunchAgent(agent.target)}
                className="mt-4 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-md shadow-emerald-600/20 flex items-center justify-center gap-2 cursor-pointer hover:scale-[1.02]"
              >
                <span>{isPl ? '🚀 Uruchom Zadanie' : isUk ? '🚀 Виконати Задачу' : '🚀 Execute Task'}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

